import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { OfflineReceipt } from './offline-receipt.js';

export enum OfflineReceiptActionType {
  Create = 'CREATE',
  Cancel = 'CANCEL',
}

export class OfflineReceiptAction extends DataModel {
  static readonly TYPE_ID: string = 'c491d8650f264b55b977dd6941099892';

  private constructor(
    public readonly type: OfflineReceiptActionType,

    // DO NOT ADD MORE THAN ONE RECEIPT UNTIL LEGACY SYSTEM IS FULLY DEPRECATED!
    private readonly receipts: OfflineReceipt[],
  ) {
    super();
  }

  get isEmpty(): boolean {
    return this.receipts.length <= 0;
  }

  get numberOfReceipts(): number {
    return this.receipts.length;
  }

  get $typeId() {
    return OfflineReceiptAction.TYPE_ID;
  }

  static asCreate(receipt: OfflineReceipt): OfflineReceiptAction {
    return new OfflineReceiptAction(
        OfflineReceiptActionType.Create,
        [receipt],
    );
  }

  static asCancel(receipt: OfflineReceipt): OfflineReceiptAction {
    return new OfflineReceiptAction(
        OfflineReceiptActionType.Cancel,
        [receipt],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineReceiptAction {
    return new OfflineReceiptAction(
        toEnumFromString(MapUtils.get<string>(map, 'type'), OfflineReceiptActionType),
        MapUtils.getList<OfflineReceipt>(map, 'receipts', (value) => {
          return context.fromData<OfflineReceipt>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'receipts': this.receipts.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OfflineReceiptAction(
        this.type,
        this.receipts.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: OfflineReceiptAction): boolean {
    if(this === other) {
      return true;
    }

    return this.type === other.type &&
      isSameList(other.receipts, this.receipts, isSameData);
  }

  getReceipt(index: number): OfflineReceipt {
    return this.receipts[index];
  }

  getAllReceipts(): OfflineReceipt[] {
    return [...this.receipts];
  }

  getReceiptsForCheckout(): OfflineReceipt[] {
    return [...this.receipts];
  }

  toDiagnostics(): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'receipts': this.receipts.map((e) => e.toDiagnostics()),
    };
  }
}

export class OfflineReceiptActionBuilder extends DataModelBuilder<OfflineReceiptAction> {
  build(context: SerializationContext, map: SerializedData): OfflineReceiptAction {
    return OfflineReceiptAction.fromMap(context, map);
  }
}
